// Methodology page: a plain-language explanation of how the measurement works,
// mirroring docs/methodology.md, plus the live list of measurement nodes grouped
// by tier. The definitions here match web/src/engine/metrics.ts.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store';
import { TIER_ORDER, type Tier } from '@/engine/types';
import { Panel, TierBadge } from './components';

const TIER_DESC: Record<Tier, { where: string; points: string }> = {
  lan: {
    where: 'A box on your own network (Pi, NAS, Docker).',
    points: 'Inside your home: Wi-Fi, switch, cabling, your device.',
  },
  isp: {
    where: 'A VM inside our own ISP network (we operate it).',
    points: 'Your access link / last mile / ISP core.',
  },
  region: {
    where: 'A nearby, well-connected datacentre.',
    points: 'Peering / transit out of the ISP.',
  },
  world: {
    where: 'Nodes on other continents.',
    points: 'Long-haul transit and the effect of distance.',
  },
};

const CLASSES: { cls: string; meaning: string }[] = [
  { cls: 'ok', meaning: 'Echoed, and RTT was within the acceptable delay.' },
  { cls: 'late', meaning: 'Echoed, but RTT was above the acceptable delay.' },
  { cls: 'lostUp', meaning: 'Never echoed and not in the node receive log — lost on the uplink.' },
  { cls: 'lostDown', meaning: 'Never echoed but present in the node receive log — lost on the downlink.' },
  { cls: 'unknown', meaning: 'Never echoed and no receive log was available.' },
  { cls: 'notSent', meaning: 'Held back by transport back-pressure; never put on the wire. Never counted as loss.' },
];

function NodeList() {
  const { t } = useTranslation();
  const nodes = useStore((s) => s.nodes);
  const grouped = useMemo(() => {
    const map = new Map<Tier, typeof nodes>();
    for (const tier of TIER_ORDER) map.set(tier, []);
    for (const n of nodes) map.get(n.tier)?.push(n);
    return map;
  }, [nodes]);

  if (nodes.length === 0) {
    return <p className="muted">{t('nodes.empty')}</p>;
  }
  return (
    <div className="nodegroups">
      {TIER_ORDER.map((tier) => {
        const group = grouped.get(tier) ?? [];
        if (group.length === 0) return null;
        return (
          <div key={tier} className="nodegroup">
            <h3>
              <TierBadge tier={tier} /> {TIER_DESC[tier].where}
            </h3>
            <ul className="nodegroup__list">
              {group.map((n) => {
                const place = [n.city, n.country].filter(Boolean).join(', ');
                return (
                  <li key={n.id}>
                    <span className="nodegroup__name">{n.name}</span>
                    {place ? <span className="muted"> · {place}</span> : null}
                    {!n.healthy ? <span className="badge badge--down"> {t('nodes.unhealthy')}</span> : null}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function MethodologyPage() {
  const { t } = useTranslation();
  return (
    <div className="page methodology">
      <article className="prose">
        <h1>{t('methodology.title')}</h1>

        <h2>What the test actually does</h2>
        <p>
          Your browser opens a WebRTC data channel to a test server (a “node”) and sends a stream of
          small probe packets at a fixed rate. Each packet is echoed straight back by the node. The
          channel is configured as <strong>unordered and un-retransmitted</strong>{' '}
          (<code>ordered: false, maxRetransmits: 0</code>) — the only browser primitive that behaves
          like UDP, so a lost packet stays lost instead of being silently resent.
        </p>
        <p>
          For every packet the browser records when it was sent and, if it returns, when it came
          back — that is the round-trip time (RTT), measured entirely on your own clock, so no clock
          synchronisation with the server is needed. In parallel the node keeps a{' '}
          <strong>receive log</strong> of which sequence numbers actually reached it and streams that
          back. Comparing “did it come back?” with “did the node receive it?” is what splits{' '}
          <strong>upload loss</strong> (the node never got it) from{' '}
          <strong>download loss</strong> (the node got it, the echo never returned).
        </p>

        <h2>Per-packet classification</h2>
        <p>Every recorded packet lands in exactly one class:</p>
        <div className="tablewrap">
          <table className="doctable">
            <thead>
              <tr>
                <th>Class</th>
                <th>Meaning</th>
              </tr>
            </thead>
            <tbody>
              {CLASSES.map((c) => (
                <tr key={c.cls}>
                  <td>
                    <code>{c.cls}</code>
                  </td>
                  <td>{c.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2>How the headline numbers are calculated</h2>
        <ul>
          <li>
            <strong>Upload / download / total loss %</strong> = the matching class counts divided by
            the number of recorded packets <code>n</code>.
          </li>
          <li>
            <strong>Average latency</strong> = mean RTT over <code>ok</code> and <code>late</code>{' '}
            packets; <strong>jitter</strong> is reported both as the mean absolute consecutive RTT
            difference and as the standardised RFC 3550 figure.
          </li>
          <li>
            <strong>Percentiles</strong> p50 / p90 / p99 (plus min, max, standard deviation) over
            echoed RTTs.
          </li>
          <li>
            <strong>Wilson score interval (95 %)</strong> on the total-loss proportion — the honest
            error bar, and what the ladder uses instead of raw comparisons. With only a few hundred
            packets, “0 % loss” can really mean “0–2.5 %”, so run longer before drawing firm
            conclusions.
          </li>
          <li>
            <strong>Loss bursts</strong> (run-length distribution plus a fitted Gilbert-Elliott{' '}
            <code>p</code>/<code>q</code>) and <strong>drift</strong> (RTT slope in ms/s — a positive
            slope under constant load is the classic bufferbloat signature).
          </li>
        </ul>

        <h2>The cable tester: sequential ladder localisation</h2>
        <p>
          Reflector nodes are placed at increasing distance from you, in four tiers. Loss that first
          appears at a given tier points at that segment of the path.
        </p>
        <div className="tablewrap">
          <table className="doctable">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Where it sits</th>
                <th>Loss to this tier points at</th>
              </tr>
            </thead>
            <tbody>
              {TIER_ORDER.map((tier) => (
                <tr key={tier}>
                  <td>
                    <TierBadge tier={tier} />
                  </td>
                  <td>{TIER_DESC[tier].where}</td>
                  <td>{TIER_DESC[tier].points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="callout">
          <strong>One node at a time, back-to-back.</strong> The tiers are tested strictly
          sequentially: the test opens the LAN node, runs the full probe, tears the connection down,
          then opens the ISP node, and so on. There is <strong>never</strong> probe traffic to more
          than one node at the same moment — this keeps the network perfectly clean during each
          measurement, so the traffic used to test one tier can never contaminate another. Because the
          tiers are measured one after another, localisation is a comparison of independent per-tier
          results, never a same-instant correlation across nodes.
        </p>
        <p>
          Ordering the tiers <code>lan → isp → region → world</code>, localisation flags the first
          tier whose Wilson loss interval sits entirely above the previous tier’s. Supporting
          evidence comes from upload-only vs download-only loss, a packet-size loss threshold (an MTU
          / fragmentation signature, visible in the MTU sweep), and positive latency drift
          (bufferbloat).
        </p>

        <h2>Honest caveats</h2>
        <ul>
          <li>
            <strong>SCTP fragmentation above ~1,150 B of payload.</strong> Browsers cap the path MTU
            at about 1,200 bytes and split larger messages into several datagrams; losing any one
            loses the whole message. Read large-size results as a signal (“1,400 B never arrives while
            1,200 B always does → an MTU problem”), not a literal datagram-loss percentage.
          </li>
          <li>
            <strong>No clock sync, so everything is RTT-based.</strong> We can split upstream from
            downstream <em>loss</em> via the receive log, but we deliberately do not report one-way
            latency, which would be a guess.
          </li>
          <li>
            <strong>Browser scheduling jitter.</strong> Timers are throttled in background tabs or on
            a busy laptop; that jitter comes from your device, not the network. Keep the tab in the
            foreground for the cleanest numbers.
          </li>
          <li>
            <strong>Statistical uncertainty on short runs.</strong> Every loss figure carries its
            Wilson interval; localisation claims require enough packets for the intervals to separate.
          </li>
        </ul>
      </article>

      <Panel title={t('methodology.nodesTitle')}>
        <NodeList />
      </Panel>
    </div>
  );
}
